import {
    ChangeDetectorRef,
    Directive,
    ElementRef,
    OnChanges,
    SimpleChanges,
} from "@angular/core";

import { GridFacade, GridFilter } from "@hermes/states/grid-filters-state";
import { SanitizePipe } from "@hermes/utils-generic/pipes/sanitize";
import { FocusService } from "@hermes/utils-generic/services/focus";

import { ApolloFilterComponent } from "./apollo-filter.component";

/**
 * Abstract component for the GridFilter checkbox and block behavior
 */
@Directive()
export abstract class AbstractFilterCheckboxComponent
    extends ApolloFilterComponent
    implements OnChanges
{
    /** Available filters */
    public filtersAvailable: GridFilter[];

    /** Selected filters */
    public filtersSelected: GridFilter[];

    constructor(
        public elementReference: ElementRef,
        public sanitizePipe: SanitizePipe,
        public focusService: FocusService,
        public cd: ChangeDetectorRef,
        public gridFacade: GridFacade,
    ) {
        super(sanitizePipe);
    }

    public ngOnChanges(changes: SimpleChanges): void {
        this.checkFilterAvailable();

        this.checkFilterSelected();
    }

    /**
     * @returns an array of filters
     */
    public getFilters(): GridFilter[] {
        return this.filterGroup.filters;
    }

    /**
     * @returns the filter's group title in lower case
     */
    public getFilterGroupName(): string {
        return this.filterGroup.name.toLowerCase();
    }

    /**
     * @returns the filter label
     */
    public getFilterLabel(): string {
        return `filter-label-${this.getSanitizedFilterGroupTitle()}`;
    }

    /**
     * Display html in DOM
     */
    public canDisplayFilters(): boolean {
        return !!this.filterGroup.facetUrlParameterName || this.isExpanded;
    }

    /**
     * This function reload the content of the selectedList and availabledList
     */
    public checkAvailableAndSelectedFilters(): void {
        this.checkFilterAvailable();
        this.checkFilterSelected();
    }

    /**
     * @param filter GridFilter that contains the id
     * @returns the filter id
     */
    public inputId(filter: GridFilter): string {
        const id = `${this.getFilterGroupType()}-${this.getFilterGroupName()}-${
            filter.name
        }`;

        return this.sanitizePipe.transform(id);
    }

    /**
     * This function set the filter selected and broadcast an event to tell that criterias has changed
     *
     * @param filter GridFilter to set selected
     */
    public addFilter(filter: GridFilter): void {
        filter.selected = true;

        this.gridFacade.updateGridFilter(filter, this.filterGroup);

        const indexAvailableElement = this.filtersAvailable.indexOf(filter);
        this.checkAvailableAndSelectedFilters();

        // To change the focus on the next element
        this.focusOnNextFilterAvailable(
            filter,
            this.filtersAvailable,
            indexAvailableElement,
        );
    }

    /**
     * @returns the filter's group presentation in lower case
     */
    protected getFilterGroupType(): string {
        return this.filterGroup.presentation.toLowerCase();
    }

    /**
     * This function set the list of available filters
     */
    protected checkFilterAvailable(): void {
        this.filtersAvailable = this.filterGroup.filters.filter(
            (flt: GridFilter) => !flt.selected,
        );
    }

    /**
     * This function set the list of selected filters
     */
    protected checkFilterSelected(): void {
        this.filtersSelected = this.filterGroup.filters
            .filter((flt: GridFilter) => flt.selected)
            .sort((t1: GridFilter, t2: GridFilter) =>
                // To sort filters by title
                t1.name.localeCompare(t2.name),
            );
    }

    /**
     * To focus the next element after a selection
     *
     * @param filter the added filter
     * @param filterAvailableList the available filter list
     * @param indexAvailableElement index of the available element
     */
    protected focusOnNextFilterAvailable(
        filter: GridFilter,
        filterAvailableList: GridFilter[],
        indexAvailableElement: number,
    ): void {
        if (indexAvailableElement < filterAvailableList.length) {
            // Following the selection of an available filter, focus is on the next available filter.
            this.focusService.elementToFocus = this.inputId(
                filterAvailableList[indexAvailableElement],
            );
            return;
        }

        if (filterAvailableList.length > 0) {
            // Following the selection of the last available filter, focus is on this new selected filter.
            const indexOfNewSelectedFilter =
                this.filtersSelected.indexOf(filter);
            this.focusService.elementToFocus = `${this.inputId(
                this.filtersSelected[indexOfNewSelectedFilter],
            )}-selected`;
            return;
        }

        // If there is no more available filters, focus is on the first selected filter of the same facet.
        this.focusService.elementToFocus = `${this.inputId(
            this.filtersSelected[0],
        )}-selected`;
    }
}
